import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "delay", "title", "message", "action", "onClose", "onRemoved", "kind", "show", "dismissible", "icon"];
import React, { forwardRef, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useTimeout from '@restart/hooks/useTimeout';
import useEventCallback from '@restart/hooks/useEventCallback';
import { sizeSpacing09 } from '@resultadosdigitais/tangram-design-tokens';
import Slide from '../Slide';
import { KINDS, SIZES, convertUnitToInt } from '../helper';
import config from './config';
import { MessageContainer, MessageTitle, MessageIcon, MessageContent, MessageAction, MessageClose, MessageText } from '../MessageBase';
import { useDeprecated } from '../hooks';
const Root = styled(MessageContainer).withConfig({
  displayName: "Toast__Root",
  componentId: "sc-6olh5t-0"
})(["box-shadow:var(--shadow-md);position:fixed;top:var(--size-spacing-07);right:var(--size-spacing-07);z-index:var(--zindex-6);width:300px;border:0;> ", "{top:var(--size-spacing-02);right:var(--size-spacing-02);}"], MessageClose.Style);
const Toast = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    children,
    delay,
    title,
    message,
    action,
    onClose,
    onRemoved,
    kind,
    show,
    dismissible,
    icon
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  const savedDuration = useRef(delay);
  useEffect(() => {
    savedDuration.current = delay;
  }, [delay]);
  const handleOnClose = useEventCallback(event => onClose(event));
  const handleOnRemoved = useEventCallback(event => !show && onRemoved(event));
  const timeout = useTimeout();
  const timeoutEvent = useCallback(() => {
    if (!(delay && show)) {
      return;
    }

    handleOnClose(null);
  }, [delay, show, handleOnClose]);
  useEffect(() => {
    if (show) {
      timeout.set(timeoutEvent, savedDuration.current);
    }
  }, [show, timeout, timeoutEvent]);
  const content = children || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MessageIcon, {
    kindMessage: kind,
    size: SIZES.SM,
    icon: icon
  }), /*#__PURE__*/React.createElement(MessageContent, {
    size: SIZES.SM
  }, /*#__PURE__*/React.createElement(MessageTitle, {
    size: SIZES.SM
  }, title), /*#__PURE__*/React.createElement(MessageText, {
    size: SIZES.SM
  }, message), /*#__PURE__*/React.createElement(MessageAction, null, action)));
  const from = config.size + convertUnitToInt(sizeSpacing09);
  useDeprecated({
    action
  });
  return /*#__PURE__*/React.createElement(Slide, {
    ref: ref,
    in: show,
    from: from,
    onEnd: handleOnRemoved,
    keepMounted: false
  }, /*#__PURE__*/React.createElement(Root, _extends({
    size: SIZES.SM,
    kindMessage: kind,
    "aria-live": "assertive",
    "aria-atomic": "true"
  }, rest), content, dismissible && /*#__PURE__*/React.createElement(MessageClose, {
    size: SIZES.SM,
    onClick: handleOnClose
  })));
});
Toast.displayName = 'Toast';
Toast.Style = Root;
Toast.Title = MessageTitle;
Toast.Icon = MessageIcon;
Toast.Content = MessageContent;
Toast.Text = MessageText;
Toast.Action = MessageAction;
Toast.kinds = {
  default: KINDS.DEFAULT,
  primary: KINDS.PRIMARY,
  success: KINDS.SUCCESS,
  danger: KINDS.DANGER,
  warning: KINDS.WARNING,
  help: KINDS.HELP
};
Toast.sizes = {
  sm: SIZES.SM,
  md: SIZES.MD
};
Toast.defaultProps = {
  delay: undefined,
  message: '',
  title: '',
  kind: Toast.kinds.default,
  show: true,
  onClose: () => {},
  onRemoved: () => {},
  dismissible: true,
  children: null
};
process.env.NODE_ENV !== "production" ? Toast.propTypes = {
  /**
   * Sets the number of milliseconds to wait before automatically calling the `onClose` function. `onClose` should be passed by props.
   */
  delay: PropTypes.number,

  /**
   * Sets if content of Toast will be customizable.
   */
  message: PropTypes.string,

  /**
   * Sets the title.
   */
  title: PropTypes.string,

  /**
   * Sets the kind for styling.
   */
  kind: PropTypes.oneOf([Toast.kinds.default, Toast.kinds.primary, Toast.kinds.success, Toast.kinds.danger, Toast.kinds.warning, Toast.kinds.help]),

  /**
   * Sets when the Toast will be shown or not.
   */
  show: PropTypes.bool,

  /**
   * Callback fired when the `Toast` closure is requested, and before starting the animation, like when the close button is clicked.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onClose: PropTypes.func,

  /**
   * Callback fired when the `Toast` is completely removed (after animation).
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onRemoved: PropTypes.func,

  /**
   * Sets if the `Toast` is dismissible making available a button that when clicked closes the `Toast`.
   */
  dismissible: PropTypes.bool,

  /**
   * Sets the custom icon.
   */
  icon: PropTypes.element,

  /**
   * Sets the children.
   */
  children: PropTypes.node,

  /**
   * @deprecated
   *
   * Sets elements for action area.
   */
  action: PropTypes.node
} : void 0;
export default Toast;